"use client";

import Image from "@/app/components/ProtectedImage";
import { useState, useRef, useEffect } from "react";

type GalleryPhoto = {
  name: string;
  path: string;
  alt: string;
};

export default function ModelGallery({
  photos,
  notice,
}: {
  photos: GalleryPhoto[];
  notice?: string;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = photos[activeIdx];
  const thumbContainerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const container = thumbContainerRef.current;
    if (!container) return;
    const activeThumb = container.children[activeIdx] as HTMLElement;
    if (!activeThumb) return;
    const scrollLeft =
      activeThumb.offsetLeft -
      container.offsetWidth / 2 +
      activeThumb.offsetWidth / 2;
    container.scrollTo({ left: scrollLeft, behavior: "smooth" });
  }, [activeIdx]);

  useEffect(() => {
    const container = thumbContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (maxScroll <= 0) {
        setScrollProgress(0);
        return;
      }
      setScrollProgress(container.scrollLeft / maxScroll);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener("scroll", handleScroll);
  }, [photos.length]);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="relative w-full overflow-hidden rounded-xl bg-white h-56 sm:h-64 md:h-80">
        <div className="relative h-full w-full">
          <Image
            src={active.path}
            alt={active.alt}
            fill
            className="object-contain"
            sizes="(max-width: 768px) 100vw, 1200px"
            priority
          />
        </div>

        <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full">
          {activeIdx + 1} / {photos.length}
        </div>
      </div>

      {photos.length > 1 && (
        <div className="space-y-2">
          <div
            ref={thumbContainerRef}
            className="flex gap-2 sm:gap-2.5 overflow-x-auto pb-0.5 scrollbar-hide scroll-smooth"
          >
            {photos.map((photo, idx) => (
              <button
                key={photo.name}
                onClick={() => setActiveIdx(idx)}
                className={`relative shrink-0 rounded-xl overflow-hidden transition-all duration-200
                  w-[72px] h-[52px] sm:w-28 sm:h-20
                  ${
                    idx === activeIdx
                      ? "ring-2 ring-blue-500 shadow-md scale-[1.02] opacity-100"
                      : "opacity-50 hover:opacity-80 hover:scale-[1.01]"
                  }`}
              >
                <Image
                  src={photo.path}
                  alt={photo.alt}
                  fill
                  className="object-cover"
                  sizes="120px"
                />
              </button>
            ))}
          </div>

          <div className="sm:hidden flex justify-center">
            <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-400 rounded-full transition-all duration-150"
                style={{
                  width: "40%",
                  transform: `translateX(${scrollProgress * 150}%)`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {notice && <p className="text-xs text-gray-500">{notice}</p>}
    </div>
  );
}
